import { calcGeneratorDuration, spring } from "motion-dom";
import { animate } from "motion/mini";

/**
 * Entrance/exit micro-animations on the WAAPI-backed motion/mini engine with
 * a pregenerated spring easing - the full motion engine costs ~74KB minified
 * and buys nothing extra here. Every entrance is backed by a watchdog that
 * force-lands the final state: some embedding environments freeze or
 * virtualize animations, and the element must appear regardless - never stick
 * invisibly on its first frame.
 */

/** Strong ease-out for exits (easing.dev "ease-out-quint" family). */
const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];
const EASE_IN_OUT: [number, number, number, number] = [0.77, 0, 0.175, 1];
const PANEL_ENTER_EASE: [number, number, number, number] = [0.25, 0.8, 0.25, 1];
const PANEL_EXIT_EASE: [number, number, number, number] = [0.75, 0, 0.75, 0.2];

/** iOS app-folder style spring for entrances. Duration-based (settles at
    ~550ms) to match the original full-motion feel - the visualDuration form
    runs ~950ms and outlives the 700ms cleanup guards. mini only accepts
    easing functions, so the generator is sampled into one (pregenerated as
    linear() by the WAAPI engine). */
const springGenerator = spring({ keyframes: [0, 1], duration: 550, bounce: 0.25 });
const SPRING_DURATION_MS = calcGeneratorDuration(springGenerator) ?? 550;
const springEase = (progress: number): number =>
  springGenerator.next(SPRING_DURATION_MS * progress).value;
const SPRING_DURATION_S = SPRING_DURATION_MS / 1000;

let instantMotion = false;

/**
 * Snap every entrance/exit to its final state - for story/a11y runners where
 * axe would otherwise scan mid-animation opacity and report phantom contrast
 * violations. A typed seam, deliberately not a global side channel.
 */
export function setInstantMotion(on: boolean): void {
  instantMotion = on;
}

const prefersReducedMotion = (): boolean =>
  typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

/** WAAPI is absent in happy-dom test runs; animations degrade to instant state changes. */
const canAnimate = (el: HTMLElement): boolean => typeof el.animate === "function" && !instantMotion;

/**
 * Unconditional watchdog: controls.then() can resolve instantly while the
 * animation is still pending on a frozen rAF, so it cannot be trusted to
 * cancel the fallback. After a normally-finished animation this is a visual
 * no-op (the element already sits at its plain CSS state).
 */
const forceFinalState = (el: HTMLElement, maxMs: number): void => {
  setTimeout(() => {
    if (!el.isConnected) return;
    // An exit (popOut) may have started since; rescuing "stuck at low
    // opacity" would resurrect a bubble that is intentionally fading away.
    if (el.dataset.plExiting !== undefined) return;
    const opacity = parseFloat(getComputedStyle(el).opacity);
    if (opacity >= 0.99) {
      // Healthy finish - just drop our inline leftovers and get out of the
      // way of unrelated styling (hover scale, disabled dimming, ...).
      el.style.removeProperty("opacity");
      el.style.removeProperty("transform");
      return;
    }
    // Near-zero opacity this long after the entrance means a frozen or
    // virtualized timeline holding the first keyframe. Anything in between
    // (e.g. disabled:opacity-45) is someone else's styling - leave it alone.
    if (opacity >= 0.2) return;
    for (const animation of el.getAnimations()) {
      try {
        animation.cancel();
      } catch {
        /* ignore */
      }
    }
    el.style.removeProperty("opacity");
    el.style.removeProperty("transform");
    // Some harnesses virtualize el.animate entirely: the stuck animation is
    // invisible to getAnimations() and uncancellable. Author !important beats
    // the animation origin in the cascade, so force the final state directly.
    if (parseFloat(getComputedStyle(el).opacity) < 0.99) {
      el.style.setProperty("opacity", "1", "important");
      el.style.setProperty("transform", "none", "important");
    }
  }, maxMs);
};

export type PopInOptions = {
  /** transform-origin to animate from, e.g. "right center". */
  origin?: string;
  /** Starting transform, e.g. "translateX(24px) scale(0.3)". */
  from?: string;
  /** Stagger delay in ms. */
  delay?: number;
};

/** Spring an element in from `from` to its natural CSS state. */
export function popIn(el: HTMLElement, options: PopInOptions = {}): void {
  const { origin, delay = 0 } = options;
  if (origin) el.style.transformOrigin = origin;
  if (!canAnimate(el)) return;
  const from = prefersReducedMotion() ? "none" : (options.from ?? "scale(0.95)");
  // Hold the start state inline: while the animation is still pending (it
  // starts on the next frame) the element must not flash its final state.
  el.style.opacity = "0";
  if (from !== "none") el.style.transform = from;
  const start = (): void => {
    try {
      // Only the transform springs; a bouncy spring on opacity hovers below 1
      // through its settle and the page ghosts through the surface.
      animate(el, { opacity: [0, 1] }, { duration: 0.18, ease: "easeOut", delay: delay / 1000 });
      if (from !== "none") {
        animate(
          el,
          { transform: [from, "none"] },
          { duration: SPRING_DURATION_S, ease: springEase, delay: delay / 1000 },
        );
      }
    } catch {
      // Never leave a half-applied start state behind.
      el.style.removeProperty("opacity");
      el.style.removeProperty("transform");
    }
  };
  // WAAPI silently creates nothing for detached elements, and refs run before
  // Solid attaches the node - defer to the microtask after insertion.
  if (el.isConnected) start();
  else queueMicrotask(start);
  forceFinalState(el, delay + 700);
}

type PanelMotionDirection = "enter" | "exit";
type PanelAnchor = "left" | "right";
type PanelSourceSize = { width: number; height: number };
const panelMotionTokens = new WeakMap<HTMLElement, symbol>();

const cancelElementAnimations = (el: HTMLElement): void => {
  if (typeof el.getAnimations !== "function") return;
  for (const animation of el.getAnimations()) {
    try {
      animation.cancel();
    } catch {
      /* ignore */
    }
  }
};

/** Animate the controls as one group while preserving its launcher-side anchor. */
export function animateAnchoredPanel(
  el: HTMLElement,
  direction: PanelMotionDirection,
  anchor: PanelAnchor,
  sourceSize?: PanelSourceSize,
): Promise<void> {
  const token = Symbol();
  panelMotionTokens.set(el, token);
  const cleanup = (): void => {
    if (panelMotionTokens.get(el) !== token) return;
    el.style.removeProperty("opacity");
    el.style.removeProperty("transform");
    delete el.dataset.plExiting;
  };
  el.style.transformOrigin = `${anchor} bottom`;
  delete el.dataset.plExiting;
  cancelElementAnimations(el);
  if (!canAnimate(el)) return Promise.resolve();

  const reduced = prefersReducedMotion();
  const entering = direction === "enter";
  if (!entering) el.dataset.plExiting = "";
  const restingTransform = "scale(1)";
  const panelRect = el.getBoundingClientRect();
  const sourceScaleX = sourceSize ? Math.min(1, sourceSize.width / panelRect.width) : 0.94;
  const sourceScaleY = sourceSize ? Math.min(1, sourceSize.height / panelRect.height) : 0.94;
  const sourceTransform = `scale(${sourceScaleX}, ${sourceScaleY})`;
  const keyframes = reduced
    ? { opacity: entering ? [0, 1] : [1, 0] }
    : entering
      ? {
          opacity: [0.65, 1],
          transform: [sourceTransform, restingTransform],
        }
      : {
          opacity: [1, 0.65],
          transform: [restingTransform, sourceTransform],
        };

  try {
    const controls = animate(el, keyframes, {
      duration: reduced ? 0.15 : 0.3,
      ease: entering ? PANEL_ENTER_EASE : PANEL_EXIT_EASE,
    });
    return Promise.resolve(controls).then(cleanup, cleanup);
  } catch {
    cleanup();
    return Promise.resolve();
  }
}

/** Transition mode-dependent content without moving its surrounding card. */
const contentMotionTokens = new WeakMap<HTMLElement, symbol>();

const animateTransientContent = (
  el: HTMLElement,
  transform: [string, string] | undefined,
): void => {
  const token = Symbol();
  contentMotionTokens.set(el, token);

  const finish = (): void => {
    if (contentMotionTokens.get(el) !== token) return;
    cancelElementAnimations(el);
    el.style.removeProperty("opacity");
    el.style.removeProperty("transform");
  };

  const start = (): void => {
    if (!el.isConnected || contentMotionTokens.get(el) !== token || !canAnimate(el)) {
      finish();
      return;
    }
    cancelElementAnimations(el);
    const keyframes =
      prefersReducedMotion() || !transform ? { opacity: [0, 1] } : { opacity: [0, 1], transform };
    try {
      const controls = animate(el, keyframes, { duration: 0.15, ease: EASE_OUT });
      void Promise.resolve(controls).then(finish, finish);
      // Some hosts resolve or freeze animation timelines inconsistently. The
      // content transition is decorative, so visibility always wins.
      setTimeout(finish, 200);
    } catch {
      finish();
    }
  };

  // Solid refs can run before the node is attached. Starting WAAPI on a
  // detached node can retain its first keyframe with no animation to cancel.
  if (el.isConnected) start();
  else queueMicrotask(start);
};

export function animateContextContent(el: HTMLElement): void {
  animateTransientContent(el, ["translateY(2px)", "translateY(0)"]);
}

/** Briefly clear outgoing context copy before its replacement enters. */
export function animateContextContentExit(el: HTMLElement, onFinish: () => void): void {
  cancelElementAnimations(el);
  if (!canAnimate(el)) {
    onFinish();
    return;
  }
  const keyframes = prefersReducedMotion()
    ? { opacity: [1, 0] }
    : { opacity: [1, 0], transform: ["translateY(0)", "translateY(-2px)"] };
  try {
    const controls = animate(el, keyframes, { duration: 0.08, ease: EASE_OUT });
    void Promise.resolve(controls).then(onFinish, onFinish);
  } catch {
    onFinish();
  }
}

/** Bridge the gray context layer between measured content heights. */
export function animateContextLayerHeight(el: HTMLElement, fromHeight: number): void {
  cancelElementAnimations(el);
  el.style.removeProperty("height");
  el.style.removeProperty("overflow");
  if (!canAnimate(el) || prefersReducedMotion()) return;
  const toHeight = el.getBoundingClientRect().height;
  if (Math.abs(toHeight - fromHeight) < 1) return;

  el.style.height = `${fromHeight}px`;
  el.style.overflow = "hidden";
  const finish = (): void => {
    cancelElementAnimations(el);
    el.style.removeProperty("height");
    el.style.removeProperty("overflow");
  };
  try {
    const controls = animate(
      el,
      { height: [`${fromHeight}px`, `${toHeight}px`] },
      { duration: 0.2, ease: EASE_IN_OUT },
    );
    void Promise.resolve(controls).then(finish, finish);
    setTimeout(finish, 250);
  } catch {
    finish();
  }
}

/** Crossfade content occupying the same launcher footprint. */
export function crossfadeLauncherContent(el: HTMLElement): void {
  animateTransientContent(el, ["scale(0.95)", "scale(1)"]);
}

/**
 * Reverse of popIn; resolves when the exit finished (or immediately when
 * animation is unavailable). A fallback timer guarantees resolution so UI
 * teardown can never hang. Exits are quick tweens - springs are for arrivals.
 */
export function popOut(el: HTMLElement): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      resolve();
    };
    el.dataset.plExiting = "";
    if (!canAnimate(el)) {
      finish();
      return;
    }
    setTimeout(finish, 250);
    try {
      const keyframes = prefersReducedMotion()
        ? { opacity: [1, 0] }
        : { opacity: [1, 0], transform: ["scale(1)", "scale(0.96)"] };
      const controls = animate(el, keyframes, { duration: 0.13, ease: EASE_OUT });
      Promise.resolve(controls).then(finish, finish);
    } catch {
      finish();
    }
  });
}
